/**
 * Archive a tutor: eligibility gate, then reason + comment confirm.
 */
import * as React from 'react';
import { ConfirmModal } from '@/components/common/modals/ConfirmModal';
import { InfoModal } from '@/components/common/modals/InfoModal';
import { RadioGroup } from '@/components/forms/RadioGroup';
import { Textarea } from '@/components/forms/Textarea';
import { ARCHIVE_REASON_OPTIONS } from '@/constants/shared-catalog';
import { useArchiveTutorRemote, useGetTutorRemoteArchiveEligibility } from '@/hooks/useUsers';
import { useFormValidation } from '@/hooks/useFormValidation';
import type { ArchiveReason } from '@/types/user.types';
import { getErrorMessage } from '@/utils/errors-utils';
import { archiveRemoteValidationSchema } from '@/utils/validation-schemas-utils';
import { buildArchiveDto, isArchiveReason } from './tutor-form-utils';
import { ActivePairsBlockModal } from '@/components/common/modals/ActivePairsBlockModal';

/** Props for {@link ArchiveTutorModal}. */
interface ArchiveTutorModalProps {
  open: boolean;
  userId: string;
  companyId: string;
  tutorName?: string;
  isTutoringInProgress?: boolean;
  onClose: () => void;
}

/** Archive a tutor: eligibility gate, then reason + comment confirm. */
export const ArchiveTutorModal: React.FC<Readonly<ArchiveTutorModalProps>> = ({
  open,
  userId,
  companyId,
  tutorName = 'Ce tuteur',
  isTutoringInProgress = false,
  onClose,
}) => {
  const [reason, setReason] = React.useState<ArchiveReason | ''>('');
  const [comment, setComment] = React.useState('');
  const validation = useFormValidation(archiveRemoteValidationSchema);
  const eligibility = useGetTutorRemoteArchiveEligibility(userId, companyId, {
    enabled: open && !!userId && !isTutoringInProgress,
  });
  const archive = useArchiveTutorRemote();

  React.useEffect(() => {
    if (!open) return;
    setReason('');
    setComment('');
    validation.resetValidation();
  }, [open, userId, validation.resetValidation]);

  function handleReasonChange(value: string): void {
    if (!isArchiveReason(value)) return;
    const next = { reason: value, comment };
    setReason(value);
    validation.validateField('reason', value, next);
    validation.markAsTouched('reason');
  }

  function handleCommentChange(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    const nextComment = event.target.value;
    const next = { reason, comment: nextComment };
    setComment(nextComment);
    validation.validateField('comment', nextComment, next);
    validation.markAsTouched('comment');
  }

  async function handleConfirm(): Promise<void> {
    if (!isArchiveReason(reason)) return;
    if (!validation.validateForm({ reason, comment })) return;
    await archive.mutateAsync({ userId, companyId, dto: buildArchiveDto(reason, comment) });
  }

  if (!open) return null;

  if (isTutoringInProgress || (eligibility.isSuccess && eligibility.data.canArchive === false)) {
    return (
      <ActivePairsBlockModal
        open
        personUserId={userId}
        personName={tutorName}
        personKind="tutor"
        action="archive"
        onClose={onClose}
      />
    );
  }

  if (eligibility.isLoading) {
    return (
      <InfoModal
        open
        onClose={onClose}
        title="Archivage"
        size="md"
        sections={[{ id: 'loading', content: 'Vérification des binômes actifs…' }]}
        actions={[{ id: 'close', label: 'Fermer', onClick: onClose, preset: 'modal-cancel' }]}
      />
    );
  }

  if (eligibility.isError) {
    return (
      <InfoModal
        open
        onClose={onClose}
        title="Archivage impossible"
        size="md"
        sections={[{ id: 'error', content: getErrorMessage('archiveEligibilityFailed') }]}
        actions={[{ id: 'close', label: 'Fermer', onClick: onClose, preset: 'modal-cancel' }]}
      />
    );
  }

  return (
    <ConfirmModal
      open
      onClose={onClose}
      onConfirm={handleConfirm}
      title="Archiver ce tuteur"
      description="Choisissez le motif d'archivage."
      confirmText="Archiver"
      variant="danger"
      confirmDisabled={!validation.isFormValid()}
      isLoading={archive.isPending}
    >
      <div className="space-y-4">
        <RadioGroup
          label="Motif"
          required
          name="tutor-archive-reason"
          options={ARCHIVE_REASON_OPTIONS}
          value={reason}
          onValueChange={handleReasonChange}
          disabled={archive.isPending}
          error={validation.getFieldError('reason')}
        />
        {reason === 'other' ? (
          <Textarea
            value={comment}
            onChange={handleCommentChange}
            placeholder="Précisez le motif (200 caractères maximum)"
            rows={3}
            maxLength={200}
            showCharCount
            required
            disabled={archive.isPending}
            error={validation.getFieldError('comment')}
          />
        ) : null}
      </div>
    </ConfirmModal>
  );
};
